// Nine Lives '84 - rooms.
//
// Every room is one screen with a floor, the window Tabs came in by on
// the left wall (a stool beneath it, a sill to stand on), a task to finish
// and the broom. The broom flies at Tabs and shoves him about; it would
// rather sweep up pawprints, which he leaves running along the floor, so
// they are the way to keep it busy. Shoved through the window, he is back
// in the alley with nothing; finish the task and the room pays a bonus
// that shrinks the longer it took.

import { knockCat, updateCat, type Cat } from './cat';
import {
  BONUS_BASE,
  BONUS_MIN,
  BONUS_PER_SECOND,
  BROOM_H,
  BROOM_W,
  DT,
  EXIT_SILL_TOP,
  FPS,
  K_ACTION,
  MAX_PRINTS,
  PRINT_EVERY,
  SWEEP_SPEED,
  SWEEP_TICKS,
  WORLD_H,
  WORLD_W,
} from './constants';
import type { DifficultyParams } from './difficulty';
import { overlaps } from './physics';
import type { Rng } from './rng';
import type { Box, Fatal, GameEvent, Platform, RoomKind } from './types';

export interface Broom extends Box {
  cooldown: number;
  /** Ticks spent over the print it is sweeping. */
  sweeping: number;
  target: 'print' | 'cat' | 'none';
}

export interface Pawprint {
  x: number;
  age: number;
}

export interface RoomContext {
  rng: Rng;
  params: DifficultyParams;
  level: number;
}

/** A dog living in a room: patrols the floor, charges Tabs when he is down there. */
export interface RoomDog extends Box {
  dir: 1 | -1;
}

/**
 * Move a room dog one tick: charge Tabs if he is on the floor, otherwise
 * pace between `lo` and `hi`. Returns true if it has him.
 */
export function chaseDog(
  dog: RoomDog,
  cat: Cat,
  speed: number,
  charge: number,
  lo: number,
  hi: number,
): boolean {
  const catOnFloor = cat.y < 6 && cat.knockedTicks === 0;
  let v = speed;
  if (catOnFloor) {
    dog.dir = cat.x + cat.w / 2 > dog.x + dog.w / 2 ? 1 : -1;
    v = charge;
  }
  dog.x += dog.dir * v * DT;
  if (dog.x < lo) {
    dog.x = lo;
    dog.dir = 1;
  } else if (dog.x + dog.w > hi) {
    dog.x = hi - dog.w;
    dog.dir = -1;
  }
  return catOnFloor && overlaps(cat, dog);
}

export abstract class Room {
  abstract readonly kind: RoomKind;
  /** What the HUD calls the task counter ("mice", "ferns"). */
  abstract readonly taskName: string;
  readonly platforms: Platform[] = [];
  /** The opening in the left wall. Overlapping it while shoved = out. */
  readonly window: Box = { x: 0, y: EXIT_SILL_TOP, w: 12, h: 24 };
  readonly sill: Platform = {
    kind: 'sill',
    x: 0,
    y: EXIT_SILL_TOP - 3,
    w: 18,
    h: 3,
    oneWay: true,
  };
  /** The stool's seat: a slab you can stand on and walk beneath. */
  readonly stool: Platform = { kind: 'furniture', x: 6, y: 23, w: 20, h: 3, oneWay: true };
  readonly broom: Broom;
  prints: Pawprint[] = [];
  ticks = 0;
  done = false;
  shooed = false;
  left = false;
  /** Set when the room has killed Tabs; the game takes a life. */
  fatal: Fatal | null = null;
  /** Ticks the room asks the game to hold still for a view transition. */
  freezeRequest = 0;
  private printTimer = 0;

  constructor(protected readonly ctx: RoomContext) {
    this.platforms.push(this.sill, this.stool);
    this.broom = {
      x: WORLD_W - 40,
      y: 40,
      w: BROOM_W,
      h: BROOM_H,
      cooldown: 45,
      sweeping: 0,
      target: 'none',
    };
  }

  /** Progress toward finishing the room. */
  abstract get have(): number;
  abstract get need(): number;

  /** Put Tabs on the sill, just in from the alley. */
  spawn(cat: Cat): void {
    cat.x = 2;
    cat.y = this.sill.y + this.sill.h;
    cat.vx = 0;
    cat.vy = 0;
    cat.onGround = true;
    cat.ground = this.sill;
    cat.facing = 1;
    cat.dropTicks = 0;
    cat.knockedTicks = 0;
  }

  /** Seconds spent so far, for the HUD. */
  get seconds(): number {
    return Math.floor(this.ticks / FPS);
  }

  /** The bonus if the room were finished now. */
  get bonus(): number {
    const level = Math.min(this.ctx.level, 30);
    return Math.max(BONUS_MIN, BONUS_BASE - this.seconds * BONUS_PER_SECOND) * level;
  }

  /** Advance one tick: Tabs, the task, the prints and the broom. */
  step(cat: Cat, keys: number, events: GameEvent[]): void {
    this.ticks++;
    if (!this.catBusy(cat)) {
      updateCat(cat, keys, this.platforms, events);
    }
    this.update(cat, keys, events);
    if (this.done || this.fatal) return;
    this.updatePrints(cat, events);
    this.updateBroom(cat, events);
    if (this.catBusy(cat)) return;
    if (cat.knockedTicks > 0 && overlaps(cat, this.window)) {
      this.shooed = true;
      events.push({ kind: 'shooed' });
    } else if (cat.ground === this.sill && keys & K_ACTION) {
      this.left = true;
      events.push({ kind: 'leftRoom' });
    }
  }

  /** Room-specific behaviour, run after Tabs has moved. */
  protected abstract update(cat: Cat, keys: number, events: GameEvent[]): void;

  /** True while Tabs is out of play (crawling through the cheese, swimming). */
  protected catBusy(_cat: Cat): boolean {
    return false;
  }

  /** Whether the broom is chasing right now (not while Tabs is in the tank). */
  protected get broomActive(): boolean {
    return true;
  }

  private updatePrints(cat: Cat, events: GameEvent[]): void {
    if (this.printTimer > 0) this.printTimer--;
    // Prints stay until the broom sweeps them; past the cap Tabs leaves no more.
    const running = cat.onGround && cat.ground === null && Math.abs(cat.vx) > 30;
    if (running && this.printTimer === 0 && this.prints.length < MAX_PRINTS) {
      this.prints.push({ x: cat.x + cat.w / 2, age: 0 });
      this.printTimer = PRINT_EVERY;
      events.push({ kind: 'pawprint' });
    }
    for (const p of this.prints) p.age++;
  }

  private updateBroom(cat: Cat, events: GameEvent[]): void {
    const b = this.broom;
    if (!this.broomActive) return;
    if (b.cooldown > 0) b.cooldown--;
    const bx = b.x + b.w / 2;
    const by = b.y;

    // Nearest print first; otherwise Tabs; otherwise drift to the middle.
    let print: Pawprint | null = null;
    let best = Infinity;
    for (const p of this.prints) {
      const d = Math.abs(p.x - bx);
      if (d < best) {
        best = d;
        print = p;
      }
    }
    let tx: number;
    let ty: number;
    if (print) {
      tx = print.x;
      ty = 0;
      b.target = 'print';
    } else if (!this.catBusy(cat)) {
      tx = cat.x + cat.w / 2;
      ty = cat.y;
      b.target = 'cat';
    } else {
      tx = WORLD_W / 2;
      ty = 40;
      b.target = 'none';
    }
    const dx = tx - bx;
    const dy = ty - by;
    const dist = Math.hypot(dx, dy);
    const step = this.ctx.params.broomSpeed * (print ? SWEEP_SPEED : 1) * DT;
    if (dist > step) {
      b.x += (dx / dist) * step;
      b.y += (dy / dist) * step;
    } else {
      b.x += dx;
      b.y += dy;
    }
    b.x = Math.max(0, Math.min(WORLD_W - b.w, b.x));
    b.y = Math.max(0, Math.min(WORLD_H - b.h, b.y));

    if (print && dist <= 4) {
      if (++b.sweeping >= SWEEP_TICKS) {
        this.prints = this.prints.filter((p) => p !== print);
        b.sweeping = 0;
        events.push({ kind: 'sweep' });
      }
    } else {
      b.sweeping = 0;
    }

    if (b.cooldown === 0 && !this.catBusy(cat) && overlaps(cat, b)) {
      const dir = cat.x + cat.w / 2 < bx ? -1 : 1;
      knockCat(cat, dir * 140, 120, 'broom', events);
      b.cooldown = this.ctx.params.broomCooldown;
    }
  }
}
